import { Dispatch } from "redux";
import { Notification } from "../types/notificationTypes";
import {
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../firebase";
import { getHumanReadableTimeDifference } from "../../utils";

export const FETCH_NOTIFICATIONS_REQUEST = "FETCH_NOTIFICATIONS_REQUEST";
export const FETCH_NOTIFICATIONS_SUCCESS = "FETCH_NOTIFICATIONS_SUCCESS";
export const FETCH_NOTIFICATIONS_FAILURE = "FETCH_NOTIFICATIONS_FAILURE";

export const fetchNotificationsRequest = () => ({
  type: FETCH_NOTIFICATIONS_REQUEST,
});

export const fetchNotificationsSuccess = (notificationsData: {
  notifications: Notification[];
  totalRecords: number;
}) => ({
  type: FETCH_NOTIFICATIONS_SUCCESS,
  payload: notificationsData,
});

export const fetchNotificationsFailure = (error: string) => ({
  type: FETCH_NOTIFICATIONS_FAILURE,
  payload: error,
});

export const fetchNotificationsByUser =
  (userId: number) => async (dispatch: Dispatch) => {
    dispatch(fetchNotificationsRequest());

    try {
      const notificationsData: {
        notifications: Notification[];
        totalRecords: number;
      } = {
        notifications: [],
        totalRecords: 0,
      };
      const notifications: Notification[] = [];

      let q = query(
        collection(db, "notifications"),
        where("userId", "==", userId),
        where("isViewed", "==", false)
      );
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach(async (doc) => {
        const notificationData = doc.data() as Notification;
        notificationData.documentId = doc.id;
        notificationData.timeAgo = getHumanReadableTimeDifference(
          Date.now(),
          notificationData.createdTime
        );
        notifications.push(notificationData);
      });
      notificationsData.notifications = notifications;
      notificationsData.totalRecords = 1;
      dispatch(fetchNotificationsSuccess(notificationsData));
    } catch (error) {
      console.error(error);
      dispatch(fetchNotificationsFailure("Error fetching items."));
    }
  };

export const updateNotificationsById =
  (id: string) => async (dispatch: Dispatch) => {
    try {
      const docRef = doc(db, "notifications", id || "");
      await updateDoc(docRef, { isViewed: true });
    } catch (error) {
      console.error("Error updating document: ", error);
    }
  };

/**
 * Mark all unviewed notifications as viewed for a user when account is confirmed.
 * userId in notifications collection equals createdTime in users collection.
 * Note: Runs in unauthenticated context during email confirmation; may fail due to
 * Firestore rules. Account page auto-clears when user is verified (authenticated).
 */
export const markNotificationsAsViewedByUserId = async (
  userId: number
): Promise<void> => {
  try {
    const numericUserId = Number(userId);
    const q = query(
      collection(db, "notifications"),
      where("userId", "==", numericUserId),
      where("isViewed", "==", false)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return;
    }

    const batch = writeBatch(db);
    querySnapshot.docs.forEach((docSnap) => {
      const docRef = doc(db, "notifications", docSnap.id);
      batch.update(docRef, { isViewed: true });
    });
    await batch.commit();
  } catch (error) {
    console.error("Error marking notifications as viewed: ", error);
    // Swallow - account page will auto-clear when user visits while authenticated
  }
};
